import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

/**
 * Pagina di ritorno post-checkout Stripe per acquisto ore fatto DAL distributore stesso.
 * Verifica lo stato pagamento e mostra conferma con ore accreditate.
 */
export default function AcquistoOreSuccess() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    if (!sessionId) { setState({ status: "error" }); return; }
    let cancelled = false;
    let attempts = 0;
    async function poll() {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", data });
        } else if (attempts < 12) {
          setTimeout(poll, 2500);
        } else {
          setState({ status: "pending", data });
        }
      } catch {
        if (!cancelled) setState({ status: "error" });
      }
    }
    poll();
    // Cleanup sessionStorage after paid
    return () => {
      cancelled = true;
      try {
        sessionStorage.removeItem("pending_hours_ticket_id");
        sessionStorage.removeItem("pending_hours_session_id");
      } catch { /* ignore */ }
    };
  }, [sessionId]);

  const ticketId = (() => {
    try { return sessionStorage.getItem("pending_hours_ticket_id"); }
    catch { return null; }
  })();

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <Card className="rounded-sm border-zinc-200 bg-white p-8 max-w-md w-full text-center" data-testid="acquisto-success-card">
        <div className="flex items-center justify-center gap-2 mb-6 text-brand">
          <BrandLogo size="lg" />
          <div className="font-display text-lg font-bold tracking-tighter text-zinc-950">Wineemotion</div>
        </div>
        {state.status === "loading" && (
          <>
            <Loader2 className="h-10 w-10 text-brand animate-spin mx-auto mb-4" />
            <h2 className="font-display text-xl font-semibold tracking-tight">Verifica pagamento...</h2>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="font-display text-2xl font-semibold tracking-tight text-zinc-950">Ore accreditate</h2>
            <p className="text-sm text-zinc-600 mt-3">
              Il pagamento è stato confermato. Le ore sono ora disponibili nel tuo carnet.
            </p>
            <div className="flex gap-2 mt-6">
              {ticketId ? (
                <Button
                  className="flex-1 bg-brand hover:bg-brand-dark text-white rounded-sm"
                  onClick={() => nav(`/tickets/${ticketId}`)}
                  data-testid="acquisto-success-go-ticket"
                >
                  Vai al ticket
                </Button>
              ) : null}
              <Button
                variant="outline"
                className="flex-1 rounded-sm"
                onClick={() => nav("/tickets")}
              >
                Vai ai ticket
              </Button>
            </div>
          </>
        )}
        {state.status === "pending" && (
          <>
            <Loader2 className="h-10 w-10 text-amber-500 animate-spin mx-auto mb-4" />
            <h2 className="font-display text-xl font-semibold">Elaborazione...</h2>
            <p className="text-sm text-zinc-500 mt-2">Il pagamento sta venendo processato. Riprova a controllare tra qualche istante.</p>
          </>
        )}
        {state.status === "error" && (
          <>
            <XCircle className="h-10 w-10 text-red-500 mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="font-display text-xl font-semibold">Errore verifica</h2>
            <p className="text-sm text-zinc-500 mt-2">Contatta l&apos;assistenza con questo codice: {sessionId?.slice(-12)}</p>
          </>
        )}
      </Card>
    </div>
  );
}
